import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { registroVerificar, registroIniciar, errorCode, confirmarPago } from '../../api/canalRegistro';
import { useAuth } from '../../context/AuthContext';

/* Pantallas del alta del Canal que monta el front (reparto Guardián 2026-07-11, tramo 1a):
   - VerificarEmail  → aterrizaje del enlace del email (GET /canal/registro/verificar).
   - PagoResultado   → éxito / fallo tras el pago (Stripe hospedado).
   - CompletaPerfil  → datos fiscales POST-pago (DNI, dirección…) para factura.
   Maqueta (sin backend aún): estados simulables por ?estado=. Estética = registro del canal
   (fondo oscuro + logo blanco Montserrat + tarjeta clara). Anti-enumeración: nunca "email ya registrado". */

const TEAL = '#2C6675';
const TEAL2 = '#3C8696';

const wrap = {
  minHeight: '100vh', position: 'relative', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-start',
  background: "linear-gradient(rgba(8,20,16,.82),rgba(8,16,12,.92)), url('/fondo-canal.jpg') center/cover no-repeat, linear-gradient(160deg,#0c1f16,#06110d)",
  fontFamily: "'Montserrat', system-ui, sans-serif", padding: '30px 18px 44px',
};
const card = { background: '#fff', borderRadius: 22, boxShadow: '0 18px 50px rgba(0,0,0,.35)', width: '100%', maxWidth: 460, padding: '32px 28px 28px', boxSizing: 'border-box' };
const h1 = { margin: '0 0 8px', fontSize: 23, color: '#16323a', fontWeight: 800, textAlign: 'center' };
const sub = { margin: '0 0 22px', color: '#33474d', fontSize: 14.5, lineHeight: 1.5, textAlign: 'center' };
const lbl = { fontWeight: 700, fontSize: 13, color: '#16323a', display: 'block' };
const input = { width: '100%', height: 42, borderRadius: 10, border: '1px solid #dbe6e7', padding: '0 12px', fontSize: 14.5, outline: 'none', boxSizing: 'border-box', marginTop: 4, fontFamily: 'inherit' };
const pill = (a) => ({ height: 48, borderRadius: 12, border: 'none', width: '100%', cursor: a ? 'pointer' : 'default', fontWeight: 800, fontSize: 15.5, fontFamily: 'inherit', background: a ? `linear-gradient(135deg,${TEAL2},${TEAL})` : '#cfe0e1', color: '#fff' });
const ghost = { width: '100%', height: 44, marginTop: 12, borderRadius: 12, background: 'rgba(60,134,150,.10)', color: TEAL, border: '1px solid rgba(60,134,150,.35)', fontWeight: 600, fontSize: 14.5, cursor: 'pointer', fontFamily: 'inherit' };
const iconBox = (c) => ({ width: 66, height: 66, borderRadius: '50%', background: `${c}1f`, display: 'grid', placeItems: 'center', margin: '2px auto 18px' });
const row = { display: 'flex', gap: 12 };

function Logo() {
  const { t } = useTranslation('auth');
  return (
    <div style={{ display: 'inline-block', textAlign: 'center', color: '#fff', lineHeight: 1, fontSize: 'clamp(22px,3.6vw,38px)', marginBottom: 22 }}>
      <div style={{ display: 'inline-flex', alignItems: 'flex-start', whiteSpace: 'nowrap', fontWeight: 500, letterSpacing: '.02em' }}>
        CANAL&nbsp;PILAR&nbsp;DOMÍNGUEZ<span style={{ fontSize: '.48em', fontWeight: 500, marginLeft: '.08em', alignSelf: 'flex-start', lineHeight: 1 }}>®</span>
      </div>
      <div style={{ fontSize: '0.25em', letterSpacing: '.14em', fontWeight: 500, marginTop: '0.8em', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{t('pantallas.tagline')}</div>
    </div>
  );
}
const CheckIcon = ({ c = '#2f7d5a' }) => <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5" /></svg>;
const _qp = (k) => { try { return new URLSearchParams(window.location.search).get(k); } catch (e) { return null; } };
const _emailValido = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e || '');

// ═══ CONTRATO ═══
// QUÉ HACE: el salvavidas de estas pantallas — WhatsApp y correo, siempre visibles.
// NO SE PUEDE CAMBIAR: no dejar ninguna pantalla del alta sin él, y que la
//   dirección siga ESCRITA (no solo detrás de un enlace: mucha gente la anota o
//   la reenvía). Es la MISMA que en el resto del alta: un contacto distinto por
//   pantalla es como no tener ninguno.
// POR QUÉ EXISTE: 23-09-2026 · «/verificar-email» y «/pago-resultado» no tenían
//   ninguna forma de pedir ayuda, y son justo donde se atasca la gente.
// LO VIGILA: test_TESTAUTOPISTA_errores_del_alta_hablan_claro.py
const EMAIL_AYUDA = 'mpdonline@metodopilardominguez.com';
function AyudaAlta() {
  const { t } = useTranslation('auth');
  return (
    <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #e2eeed', textAlign: 'center' }}>
      <a href="https://wa.me/34623036731?text=Hola,%20tengo%20problemas%20para%20completar%20mi%20alta%20en%20el%20Canal"
        target="_blank" rel="noopener noreferrer"
        style={{ color: TEAL, fontSize: 14, fontWeight: 700, textDecoration: 'none' }}>
        {t('funnel.ayudaAltaCorto')}
      </a>
      <p style={{ margin: '8px 0 0', fontSize: 13.5, color: '#3a4e53' }}>
        {t('pantallas.oEscribenosA')}{' '}
        <a href={`mailto:${EMAIL_AYUDA}?subject=Ayuda%20con%20mi%20alta`}
          style={{ color: '#077069', fontWeight: 700, wordBreak: 'break-all' }}>{EMAIL_AYUDA}</a>
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export function VerificarEmail({ onContinuar = () => {}, onReenviar = () => {}, maqueta = false }) {
  const { t } = useTranslation('auth');
  // Llama a GET /canal/registro/verificar?token=. En preview/maqueta simula (?estado=caducado|invalido|ok).
  const forzado = _qp('estado');
  const token = _qp('token');
  const [estado, setEstado] = useState(forzado ? forzado : 'cargando');
  const [emailReenvio, setEmailReenvio] = useState('');
  const [reenviando, setReenviando] = useState(false);
  const [reenviado, setReenviado] = useState(false);
  const [errReenvio, setErrReenvio] = useState('');
  const reenviarDeVerdad = async () => {
    if (reenviando || !_emailValido(emailReenvio)) return;
    setReenviando(true); setErrReenvio('');
    try {
      await registroIniciar(emailReenvio);
      setReenviado(true);   // confirmación GENÉRICA: no dice si ese email existe
    } catch (err) {
      setErrReenvio(err?.response?.status === 429
        ? t('funnel.reenvioDemasiados') : t('funnel.reenvioFallo'));
    } finally { setReenviando(false); }
  };
  useEffect(() => {
    if (forzado) return undefined;
    if (maqueta) { const t = setTimeout(() => setEstado('ok'), 1100); return () => clearTimeout(t); }
    let alive = true;
    (async () => {
      try {
        const r = await registroVerificar(token);
        if (alive) setEstado(r.data?.ok ? 'ok' : 'invalido');
      } catch (err) {
        if (alive) setEstado(errorCode(err) === 'TOKEN_CADUCADO' ? 'caducado' : 'invalido');
      }
    })();
    return () => { alive = false; };
  }, [forzado, maqueta, token]);

  return (
    <div style={wrap}>
      <Logo />
      <div style={{ ...card, textAlign: 'center' }}>
        {estado === 'cargando' && (
          <>
            <style>{'@keyframes vc-spin{to{transform:rotate(360deg)}}'}</style>
            <div style={{ width: 34, height: 34, borderRadius: '50%', border: '3px solid #d9e8e7', borderTopColor: TEAL, margin: '6px auto 16px', animation: 'vc-spin .9s linear infinite' }} />
            <p style={{ margin: 0, color: '#33474d', fontSize: 14.5 }}>{t('pantallas.verificando')}</p>
          </>
        )}
        {estado === 'ok' && (
          <>
            <div style={iconBox('#2f7d5a')}><CheckIcon /></div>
            <h1 style={h1}>{t('pantallas.emailVerificado')}</h1>
            <p style={sub}>{t('pantallas.emailVerificadoSub')}</p>
            <button style={pill(true)} onClick={onContinuar}>{t('comun.continuar')}</button>
          </>
        )}
        {(estado === 'caducado' || estado === 'invalido') && (
          <>
            <div style={iconBox('#d9534f')}><svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#d9534f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 8v5M12 16h.01" /></svg></div>
            <h1 style={h1}>{t('pantallas.enlaceNoValido')}</h1>
            <p style={sub}>{estado === 'caducado' ? t('pantallas.enlaceCaducado') : t('pantallas.enlaceUsado')}{t('pantallas.teEnviamosNuevo')}</p>
            {/* ═══ CONTRATO ═══
                QUÉ HACE: reenvía de VERDAD el correo de verificación.
                NO SE PUEDE CAMBIAR: no volver a colgar este botón de una simple
                  navegación. Hasta el 23-09-2026 «Reenviar email» solo llevaba a
                  la home (App.js:288): decía una cosa y hacía otra, y la persona
                  se quedaba esperando un correo que nadie había pedido.
                  Tampoco quitar el campo de correo: si el enlace caducó, la
                  verificación falla y NO sabemos de quién es el token, así que
                  hay que preguntárselo. Y la confirmación debe seguir siendo
                  GENÉRICA (anti-enumeración): nunca decir si ese email existe.
                POR QUÉ EXISTE: recorrido de pantalla del alta, 23-09-2026.
                LO VIGILA: test_TESTAUTOPISTA_errores_del_alta_hablan_claro.py */}
            {reenviado ? (
              <p style={{ ...sub, margin: 0, color: '#2f7d5a', fontWeight: 600 }}>{t('pantallas.reenvioHecho')}</p>
            ) : (
              <>
                <label style={{ ...lbl, textAlign: 'left' }}>{t('pantallas.tuCorreo')}</label>
                <input type="email" value={emailReenvio} onChange={(e) => setEmailReenvio(e.target.value.trim())}
                  placeholder={t('comun.emailPlaceholder')} style={input} />
                {errReenvio && <p style={{ color: '#d9534f', fontSize: 12.5, margin: '6px 0 0', textAlign: 'left' }}>{errReenvio}</p>}
                <button style={{ ...pill(_emailValido(emailReenvio) && !reenviando), marginTop: 14 }}
                  disabled={!_emailValido(emailReenvio) || reenviando} onClick={reenviarDeVerdad}>
                  {reenviando ? t('funnel.procesando') : t('pantallas.reenviarEmail')}
                </button>
              </>
            )}
            <button onClick={onReenviar} style={ghost}>{t('comun.volver')}</button>
          </>
        )}
        <AyudaAlta />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
/* PagoResultado · página «Pago recibido» REAL (Ann 2026-07-18).
   Es a donde vuelve el comprador tras pagar en el checkout de la web. Cierra el
   circuito comprar→pagar→ENTRAR desde el propio CRM:
     - Si ya trae sesión (mismo navegador del alta): confirma el pago y entra.
     - Si no: le pide entrar con el email y la contraseña que creó en el alta;
       al entrar, confirma el pago y le abre el Canal.
   La confirmación llama a POST /canal/registro/confirmar-pago (pregunta a la
   plataforma). Si el pago aún se está procesando, muestra «lo estamos
   confirmando» con un botón para reintentar — nunca deja al comprador colgado. */
export function PagoResultado({ onEntrar = () => {}, onVolver = () => {} }) {
  const { t } = useTranslation('auth');
  const { user, login } = useAuth();
  // fase: cargando | login | ok | pendiente
  const [fase, setFase] = useState('cargando');
  const [email, setEmail] = useState('');
  const [pw, setPw] = useState('');
  const [err, setErr] = useState('');
  const [enviando, setEnviando] = useState(false);

  // Al montar (y cada vez que aparece sesión): si hay usuario, confirmamos el
  // pago; si no, pedimos entrar. Deps = [user] (satisface exhaustive-deps).
  useEffect(() => {
    let vivo = true;
    (async () => {
      const haySesion = !!user || !!localStorage.getItem('token');
      if (!haySesion) { if (vivo) setFase('login'); return; }
      try {
        const r = await confirmarPago();
        if (vivo) setFase(r?.data?.acceso ? 'ok' : 'pendiente');
      } catch (e) {
        if (vivo) setFase('pendiente');
      }
    })();
    return () => { vivo = false; };
  }, [user]);

  const entrarYConfirmar = async (e) => {
    if (e) e.preventDefault();
    if (enviando) return;
    setErr('');
    if (!email.trim() || !pw) { setErr(t('pantallas.pagoLoginFalta')); return; }
    setEnviando(true);
    try {
      await login(email.trim().toLowerCase(), pw);
      // login() actualiza `user` → el useEffect de arriba confirma el pago.
      setFase('cargando');
    } catch (e2) {
      setErr(t('pantallas.pagoLoginMal'));
    } finally {
      setEnviando(false);
    }
  };

  const reconfirmar = async () => {
    setFase('cargando');
    try {
      const r = await confirmarPago();
      setFase(r?.data?.acceso ? 'ok' : 'pendiente');
    } catch (e) {
      setFase('pendiente');
    }
  };

  return (
    <div style={wrap}>
      <Logo />
      <div style={{ ...card, textAlign: 'center' }}>
        {fase === 'cargando' && (
          <>
            <div style={iconBox('#2f7d5a')}><CheckIcon /></div>
            <h1 style={h1}>{t('pantallas.pagoRecibido')}</h1>
            <p style={sub}>{t('pantallas.pagoConfirmando')}</p>
          </>
        )}

        {fase === 'ok' && (
          <>
            <div style={iconBox('#2f7d5a')}><CheckIcon /></div>
            <h1 style={h1}>{t('pantallas.pagoRecibido')}</h1>
            <p style={sub}>{t('pantallas.pagoOkYaEntras')}</p>
            <button style={pill(true)} onClick={onEntrar}>{t('pantallas.entrarCanal')}</button>
          </>
        )}

        {fase === 'login' && (
          <form onSubmit={entrarYConfirmar}>
            <div style={iconBox('#2f7d5a')}><CheckIcon /></div>
            <h1 style={h1}>{t('pantallas.pagoRecibido')}</h1>
            <p style={sub}>{t('pantallas.pagoEntraConTuCuenta')}</p>
            <div style={{ textAlign: 'left', marginBottom: 12 }}>
              <label style={lbl}>{t('pantallas.email')}</label>
              <input style={input} type="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tu@email.com" />
            </div>
            <div style={{ textAlign: 'left', marginBottom: 8 }}>
              <label style={lbl}>{t('pantallas.password')}</label>
              <input style={input} type="password" autoComplete="current-password" value={pw} onChange={(e) => setPw(e.target.value)} />
            </div>
            {err && <p style={{ color: '#d9534f', fontSize: 13, margin: '6px 0 0', textAlign: 'left' }}>{err}</p>}
            <button style={{ ...pill(!enviando), marginTop: 14 }} type="submit" disabled={enviando}>
              {enviando ? t('pantallas.entrando') : t('pantallas.entrarCanal')}
            </button>
          </form>
        )}

        {fase === 'pendiente' && (
          <>
            <div style={iconBox('#3C8696')}>
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="#3C8696" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>
            </div>
            <h1 style={h1}>{t('pantallas.pagoRecibido')}</h1>
            <p style={sub}>{t('pantallas.pagoPendienteSub')}</p>
            <button style={pill(true)} onClick={reconfirmar}>{t('pantallas.pagoReintentarConfirmar')}</button>
            <button style={ghost} onClick={onVolver}>{t('comun.volver')}</button>
          </>
        )}
        {/* Esta pantalla es la de después de pagar: si algo ha ido mal, es EL
            momento en el que más falta hace saber a quién escribir. */}
        <AyudaAlta />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
export function CompletaPerfil({ onGuardar = () => {}, onAhoraNo = () => {} }) {
  const { t } = useTranslation('auth');
  const [d, setD] = useState({ segundo_apellido: '', dni: '', telefono: '', direccion: '', numero_piso: '', cp: '', ciudad: '', provincia: '' });
  const set = (k) => (e) => setD({ ...d, [k]: e.target.value });
  return (
    <div style={wrap}>
      <Logo />
      <div style={{ ...card, maxWidth: 560 }}>
        <h1 style={h1}>{t('pantallas.perfilTitulo')}</h1>
        <p style={sub}>{t('pantallas.perfil1')}<b>{t('pantallas.perfilStrong')}</b>{t('pantallas.perfil2')}</p>

        <label style={lbl}>{t('comun.segundoApellido')}</label>
        <input value={d.segundo_apellido} onChange={set('segundo_apellido')} placeholder={t('comun.segundoApellido')} style={input} />
        <div style={{ ...row, marginTop: 12 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{t('comun.dniNif')}</label><input value={d.dni} onChange={set('dni')} placeholder="12345678Z" style={input} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('comun.telefono')}</label><input value={d.telefono} onChange={set('telefono')} placeholder="600 000 000" style={input} /></div>
        </div>
        <div style={{ marginTop: 12 }}>
          <div style={row}>
            <div style={{ flex: 2 }}><label style={lbl}>{t('pantallas.direccionCalle')}</label><input value={d.direccion} onChange={set('direccion')} placeholder={t('comun.calleNumeroPlaceholder')} style={input} /></div>
            <div style={{ flex: 1 }}><label style={lbl}>{t('comun.numPiso')}</label><input value={d.numero_piso} onChange={set('numero_piso')} placeholder="3º 2ª" style={input} /></div>
          </div>
        </div>
        <div style={{ ...row, marginTop: 12 }}>
          <div style={{ flex: 1 }}><label style={lbl}>{t('comun.cPostal')}</label><input value={d.cp} onChange={set('cp')} placeholder="08000" style={input} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('comun.ciudad')}</label><input value={d.ciudad} onChange={set('ciudad')} placeholder="Barcelona" style={input} /></div>
          <div style={{ flex: 1 }}><label style={lbl}>{t('comun.provincia')}</label><input value={d.provincia} onChange={set('provincia')} placeholder="Barcelona" style={input} /></div>
        </div>

        <div style={{ marginTop: 20 }}>
          <button style={pill(true)} onClick={() => onGuardar(d)}>{t('pantallas.guardarDatos')}</button>
          <button style={ghost} onClick={onAhoraNo}>{t('pantallas.ahoraNo')}</button>
        </div>
      </div>
    </div>
  );
}
